/**
 * Stats Section - Company Achievements
 * =====================================
 * Animated counter stats with particles background
 * Similar to Protocol section style
 */

import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import './Stats.css';

const statsData = [
  {
    id: 1,
    value: 50,
    suffix: '+',
    label: 'Projects Delivered',
    icon: 'fa-rocket',
    color: '#D4A853'
  },
  {
    id: 2,
    value: 100,
    suffix: '%',
    label: 'Client Satisfaction',
    icon: 'fa-smile',
    color: '#10B981'
  },
  {
    id: 3,
    value: 15,
    suffix: '+',
    label: 'Blockchain Networks',
    icon: 'fa-cubes',
    color: '#00D4FF'
  },
  {
    id: 4,
    value: 24,
    suffix: '/7',
    label: 'Support Available',
    icon: 'fa-headset',
    color: '#A855F7'
  },
  {
    id: 5,
    value: 100,
    suffix: '%',
    label: 'Secure & Audited',
    icon: 'fa-shield-alt',
    color: '#F97316'
  }
];

// Animated Counter Component
const AnimatedCounter = ({ value, suffix, duration = 2, inView }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    if (!inView) return;

    const obj = { value: 0 };
    gsap.to(obj, {
      value: value,
      duration: duration,
      ease: 'power2.out',
      onUpdate: () => {
        setCount(Math.round(obj.value));
      }
    });
  }, [value, duration, inView]);

  return (
    <span ref={countRef}>
      {count}{suffix}
    </span>
  );
};

// Particles Background
const StatsParticles = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const createParticles = () => {
      const count = window.innerWidth < 768 ? 30 : 60;
      particles = [];
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 2 + 1,
          speedX: (Math.random() - 0.5) * 0.5,
          speedY: (Math.random() - 0.5) * 0.5,
          opacity: Math.random() * 0.5 + 0.2
        });
      }
    };

    const animate = () => {
      ctx.fillStyle = 'rgba(5, 5, 5, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach(p => {
        p.x += p.speedX;
        p.y += p.speedY;

        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 168, 83, ${p.opacity})`;
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    resize();
    createParticles();
    animate();

    window.addEventListener('resize', () => {
      resize();
      createParticles();
    });

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="stats-canvas" />;
};

const Stats = () => {
  const sectionRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [animationTriggered, setAnimationTriggered] = useState(false);
  const cardRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animationTriggered) {
          setIsInView(true);
          setAnimationTriggered(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [animationTriggered]);

  useEffect(() => {
    if (!isInView) return;

    // Animate cards flying in from different directions
    cardRefs.current.forEach((card, index) => {
      if (card) {
        const directions = [
          { x: -100, y: -50 },
          { x: 0, y: -80 },
          { x: 100, y: -50 },
          { x: -80, y: 50 },
          { x: 80, y: 50 }
        ];
        const dir = directions[index] || { x: 0, y: 50 };

        gsap.fromTo(card,
          { opacity: 0, x: dir.x, y: dir.y, scale: 0.8 },
          {
            opacity: 1,
            x: 0,
            y: 0,
            scale: 1,
            duration: 0.8,
            delay: 0.3 + index * 0.15,
            ease: 'back.out(1.4)'
          }
        );
      }
    });
  }, [isInView]);

  return (
    <section className="stats-section" ref={sectionRef} id="stats">
      {/* Background - No particles */}
      <div className="stats-bg">
        <div className="stats-grid-bg" />
        <div className="stats-vignette" />
      </div>

      <div className="stats-container">
        {/* Header */}
        <div className={`stats-header ${isInView ? 'in-view' : ''}`}>
          <div className="section-badge">
            <span className="badge-dot" />
            <span>Our Track Record</span>
          </div>
          <h2 className="section-title">
            Trusted by <span className="text-highlight">Industry Leaders</span>
          </h2>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          {statsData.map((stat, index) => (
            <div
              key={stat.id}
              ref={el => cardRefs.current[index] = el}
              className="stat-card"
              style={{ opacity: 0 }}
            >
              <div className="stat-glow" style={{ background: `radial-gradient(circle, ${stat.color}20 0%, transparent 70%)` }} />
              <div className="stat-icon" style={{ color: stat.color, borderColor: `${stat.color}40`, background: `${stat.color}15` }}>
                <i className={`fas ${stat.icon}`} />
              </div>
              <div className="stat-value" style={{ color: stat.color }}>
                <AnimatedCounter value={stat.value} suffix={stat.suffix} inView={isInView} />
              </div>
              <div className="stat-label">{stat.label}</div>
              <div className="stat-ring" style={{ borderColor: `${stat.color}30` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;
